// Precondition: None
// Postcondition: Custom element for displaying suggestion chips with optional icons
// Invariants: Dispatches 'suggestion-click' event when chip is clicked

import { icon, iconNames, type IconName } from "../../../shared/icons.js";

export interface Suggestion {
  text: string;
  icon?: IconName;
  url?: string;
}

if (!customElements.get("ft-chat-suggestions")) {
  const DEFAULT_SUGGESTIONS: Suggestion[] = [
    { text: "Recent Filings", icon: "file-text" },
    { text: "Portfolio Analysis", icon: "bar-chart" },
    { text: "Find Managers", icon: "search" },
    { text: "Track Changes", icon: "trending-up" },
    { text: "Popular", icon: "star" },
  ];

  class FtChatSuggestions extends HTMLElement {
    private suggestions: Suggestion[] = DEFAULT_SUGGESTIONS;

    constructor() {
      super();
      this.attachShadow({ mode: "open" });
    }

    static get observedAttributes(): string[] {
      return ["hidden", "suggestions", "base-url"];
    }

    connectedCallback(): void {
      this.parseSuggestionsAttribute();
      this.render();
      this.setupEventListeners();
    }

    attributeChangedCallback(name: string): void {
      if (name === "suggestions" && this.shadowRoot?.querySelector(".suggestions")) {
        this.parseSuggestionsAttribute();
        const container = this.shadowRoot.querySelector(".suggestions");
        if (container) {
          container.innerHTML = this.renderChips();
        }
      }
    }

    private parseSuggestionsAttribute(): void {
      const attr = this.getAttribute("suggestions");
      if (attr) {
        try {
          const parsed = JSON.parse(attr);
          if (Array.isArray(parsed)) {
            this.suggestions = parsed.map((s) => {
              if (typeof s === "string") return { text: s };
              // Validate icon name, omit if invalid
              const validIcon = s.icon && iconNames.includes(s.icon) ? s.icon : undefined;
              return { ...s, icon: validIcon };
            });
          }
        } catch {
          // Keep defaults on parse error
        }
      }
    }

    private render(): void {
      const style = document.createElement("style");
      style.textContent = this.getStyles();

      const template = document.createElement("template");
      template.innerHTML = this.getTemplate();

      if (this.shadowRoot) {
        this.shadowRoot.appendChild(style);
        this.shadowRoot.appendChild(template.content.cloneNode(true));
      }
    }

    private getStyles(): string {
      return `
        :host {
          display: block;
        }

        :host([hidden]) {
          display: none;
        }

        .suggestions {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 10px;
          padding: 16px 0;
        }

        .chip {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          background: var(--input-bg, #ffffff);
          border: 1px solid var(--card-border, #E2E4E8);
          border-radius: var(--radius-pill, 9999px);
          font: 500 14px/1 var(--font-sans, Inter, system-ui, sans-serif);
          color: var(--text, #1C1C1E);
          text-decoration: none;
          cursor: pointer;
          transition: all 0.15s ease;
          white-space: nowrap;
        }

        .chip svg {
          width: 18px;
          height: 18px;
          color: var(--muted, #555555);
          flex-shrink: 0;
          transition: color 0.15s ease;
        }

        .chip:hover {
          border-color: var(--primary, #E0524D);
          color: var(--primary, #E0524D);
        }

        .chip:hover svg {
          color: var(--primary, #E0524D);
        }

        .chip:active {
          transform: scale(0.97);
        }

        /* Slotted anchor styling */
        ::slotted(a) {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 18px;
          background: var(--input-bg, #ffffff);
          border: 1px solid var(--card-border, #E2E4E8);
          border-radius: var(--radius-pill, 9999px);
          font: 500 14px/1 var(--font-sans, Inter, system-ui, sans-serif);
          color: var(--text, #1C1C1E);
          text-decoration: none;
          cursor: pointer;
          transition: all 0.15s ease;
          white-space: nowrap;
        }

        ::slotted(a:hover) {
          border-color: var(--primary, #E0524D);
          color: var(--primary, #E0524D);
        }

        ::slotted(a:active) {
          transform: scale(0.97);
        }

        @media (prefers-color-scheme: dark) {
          ::slotted(a) {
            background: var(--surface, #14161a);
            border-color: var(--card-border, #24262c);
            color: var(--text, #ffffff);
          }

          ::slotted(a:hover) {
            background: var(--tab-active-bg, #1a1c22);
          }
        }

        @media (prefers-color-scheme: dark) {
          .chip {
            background: var(--surface, #14161a);
            border-color: var(--card-border, #24262c);
            color: var(--text, #ffffff);
          }

          .chip svg {
            color: var(--muted, #a3a3a3);
          }

          .chip:hover {
            background: var(--tab-active-bg, #1a1c22);
          }
        }
      `;
    }

    private getTemplate(): string {
      return `<div class="suggestions">${this.renderChips()}<slot></slot></div>`;
    }

    private renderChips(): string {
      const baseUrl = this.getAttribute("base-url") || "/chat";
      return this.suggestions
        .map((s) => {
          const iconHtml = s.icon ? icon(s.icon, 18) : "";
          const href = s.url || `${baseUrl}?q=${encodeURIComponent(s.text)}`;
          return `<a href="${this.escapeAttr(href)}" class="chip" data-text="${this.escapeAttr(s.text)}">${iconHtml}${this.escapeHtml(s.text)}</a>`;
        })
        .join("");
    }

    private escapeHtml(text: string): string {
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    }

    private escapeAttr(text: string): string {
      return text.replace(/"/g, "&quot;");
    }

    private setupEventListeners(): void {
      this.shadowRoot?.addEventListener("click", (e) => {
        const chip = (e.target as HTMLElement).closest(".chip") as HTMLElement;
        if (chip) {
          e.preventDefault();
          const text = chip.dataset.text || chip.textContent?.trim() || "";
          const url = chip.dataset.url || undefined;
          this.dispatchEvent(
            new CustomEvent("suggestion-click", {
              detail: { text, url },
              bubbles: true,
              composed: true,
            }),
          );
        }
      });
    }

    // Public API to update suggestions (backward compatible with string[])
    setSuggestions(suggestions: (string | Suggestion)[]): void {
      this.suggestions = suggestions.map((s) =>
        typeof s === "string" ? { text: s } : s
      );
      const container = this.shadowRoot?.querySelector(".suggestions");
      if (container) {
        container.innerHTML = this.renderChips();
      }
    }
  }

  customElements.define("ft-chat-suggestions", FtChatSuggestions);
}
